import axios from "axios";
import { getDatabase, ref, get, update } from "firebase/database";

export const FACEBOOK_COMMENT_REQUIRED_SCOPES = [
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_engagement",
  "pages_read_user_content",
  "pages_manage_metadata",
];

export const FACEBOOK_MANAGEMENT_SCOPE =
  "public_profile,pages_manage_posts,pages_read_engagement,pages_show_list,pages_manage_engagement,pages_read_user_content,pages_manage_metadata,business_management";

export const buildCommentAutomationMetadata = (grantedScopes = []) => {
  const normalizedScopes = Array.from(
    new Set((grantedScopes || []).filter(Boolean))
  );

  return {
    grantedScopes: normalizedScopes,
    webhookSubscribed: false,
    webhookSubscribedAt: null,
    commentAutomationEligible: FACEBOOK_COMMENT_REQUIRED_SCOPES.every(
      (scope) => normalizedScopes.includes(scope)
    ),
    commentAutomationPaused: false,
    commentAutomationPauseReason: null,
  };
};

export const fetchGrantedFacebookScopes = async (userAccessToken) => {
  if (!userAccessToken) {
    return [];
  }

  try {
    const response = await axios.get(
      "https://graph.facebook.com/v21.0/me/permissions",
      {
        params: {
          access_token: userAccessToken,
        },
        timeout: 15000,
      }
    );

    return (response.data?.data || [])
      .filter((permission) => permission.status === "granted")
      .map((permission) => permission.permission)
      .filter(Boolean);
  } catch (error) {
    console.error("Không thể lấy granted scopes từ Facebook:", error);
    return [];
  }
};

export const buildConnectedFanpageRecord = (
  page,
  {
    accessToken,
    grantedScopes = [],
    tokenStatus = "active",
    tokenExpiry = null,
    tokenError = null,
  } = {}
) => {
  const metadata = buildCommentAutomationMetadata(grantedScopes);
  const record = {
    id: page.id,
    name: page.name,
    access_token: accessToken ?? page.access_token ?? null,
    category: page.category || "",
    connectedAt: Date.now(),
    lastRefreshed: Date.now(),
    tokenStatus,
    tokenExpiry,
    ...metadata,
  };

  if (tokenError) {
    record.tokenError = tokenError;
  }

  return record;
};

/**
 * Chuyển đổi short-lived USER token thành long-lived USER token
 * @param {string} accessToken - Short-lived USER token cần chuyển đổi
 * @returns {Promise<Object>} - Kết quả chuyển đổi với long-lived USER token
 */
export const exchangeForLongLivedToken = async (accessToken) => {
  try {
    const clientId = process.env.REACT_APP_FB_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_FB_CLIENT_SECRET;

    if (!clientId || !clientSecret) {
      console.error("Facebook App ID hoặc App Secret không được cấu hình");
      return { success: false, error: "Thiếu thông tin cấu hình Facebook App" };
    }

    // Sử dụng endpoint chính thức của Facebook để đổi lấy long-lived USER token
    // Tham khảo: https://developers.facebook.com/docs/facebook-login/guides/access-tokens/
    const response = await axios.get(
      `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${clientId}&client_secret=${clientSecret}&fb_exchange_token=${accessToken}`
    );

    if (response.data && response.data.access_token) {
      console.log("Đã nhận long-lived USER token:", response.data);

      // Kiểm tra expires_in có tồn tại và là số hợp lệ không
      const expiresIn = response.data.expires_in;
      const isValidExpiresIn =
        expiresIn !== undefined && expiresIn !== null && !isNaN(expiresIn);

      // Tính thời gian hết hạn cho long-lived user token
      // Long-lived user token thường có thời hạn 60 ngày theo tài liệu của Facebook
      const expiresAt = isValidExpiresIn
        ? Date.now() + expiresIn * 1000
        : Date.now() + 60 * 24 * 60 * 60 * 1000; // 60 ngày

      return {
        success: true,
        accessToken: response.data.access_token,
        expiresIn: isValidExpiresIn ? expiresIn : null,
        expiresAt: expiresAt,
      };
    }

    return { success: false, error: "Không thể chuyển đổi token" };
  } catch (error) {
    console.error("Error exchanging token:", error);
    return {
      success: false,
      error: error.response?.data?.error?.message || error.message,
    };
  }
};

/**
 * Lấy mã truy cập Trang dài hạn (Long-lived Page Access Token) từ mã truy cập Người dùng dài hạn
 * @param {string} userAccessToken - Long-lived USER token đã được chuyển đổi
 * @param {string} pageId - ID của trang cần lấy token
 * @returns {Promise<Object>} - Kết quả với long-lived PAGE token
 */
export const getPageAccessToken = async (userAccessToken, pageId) => {
  try {
    // Sử dụng Graph API để lấy danh sách trang mà người dùng quản lý
    // Mỗi trang sẽ có một page access token riêng
    // Sử dụng axios thay vì FB.api để tránh hang khi không có SDK session
    const response = await axios.get(
      `https://graph.facebook.com/v21.0/me/accounts`,
      {
        params: { access_token: userAccessToken },
        timeout: 15000,
      }
    );

    if (!response.data || response.data.error) {
      throw response.data?.error || new Error("Không thể lấy danh sách trang");
    }

    // Tìm trang cần lấy token
    const page = response.data.data.find((p) => p.id === pageId);

    if (page && page.access_token) {
      return {
        success: true,
        accessToken: page.access_token,
        expiresAt: null,
      };
    } else {
      throw new Error(`Không tìm thấy trang với ID ${pageId}`);
    }
  } catch (error) {
    console.error("Error getting page access token:", error);
    return {
      success: false,
      error: error.message || "Không thể lấy page access token",
    };
  }
};

/**
 * Kiểm tra tính hợp lệ của token
 * @param {string} accessToken - Token cần kiểm tra
 * @returns {Promise<Object>} - Kết quả kiểm tra
 */
export const validateToken = async (accessToken) => {
  try {
    // Kiểm tra xem accessToken có phải là JWT token hay không
    if (!accessToken || typeof accessToken !== "string") {
      console.warn("⚠️ validateToken: Access token không hợp lệ:", {
        accessToken,
        type: typeof accessToken,
      });
      return {
        isValid: false,
        error: "Token không hợp lệ hoặc không tồn tại",
      };
    }

    if (accessToken.trim().length === 0) {
      console.warn("⚠️ validateToken: Access token rỗng");
      return {
        isValid: false,
        error: "Access token rỗng",
      };
    }

    // Facebook access token không có dấu "." (JWT format)
    // Nếu có dấu ".", đây là JWT token của Firebase, không phải Facebook token
    if (accessToken.includes(".")) {
      console.error("❌ JWT token provided instead of Facebook access token");
      return {
        isValid: false,
        error: "JWT token không thể sử dụng cho Facebook API",
      };
    }

    // Sử dụng App Access Token để validate
    // App Access Token format: app_id|app_secret
    const clientId = process.env.REACT_APP_FB_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_FB_CLIENT_SECRET;

    if (!clientId || !clientSecret) {
      console.error("Facebook App ID hoặc App Secret không được cấu hình");
      return {
        isValid: false,
        error: "Thiếu thông tin cấu hình Facebook App",
      };
    }

    const appAccessToken = `${clientId}|${clientSecret}`;

    console.log("🔍 Validating token with App Access Token:", {
      inputTokenLength: accessToken.length,
      inputTokenPreview: accessToken.substring(0, 20) + "...",
      appAccessTokenPreview: appAccessToken.substring(0, 20) + "...",
    });

    const response = await axios.get(
      `https://graph.facebook.com/debug_token?input_token=${accessToken}&access_token=${appAccessToken}`,
      { timeout: 10000 } // 10s timeout - tránh hang vĩnh viễn
    );

    console.log("✅ validateToken response received:", {
      isValid: response.data?.data?.is_valid,
      type: response.data?.data?.type,
    });

    if (response.data && response.data.data) {
      // Kiểm tra xem token có phải là Page Access Token không
      const isPageToken = response.data.data.type === "PAGE";

      // Kiểm tra data_access_expires_at có tồn tại và là số hợp lệ không
      const dataAccessExpiresAt = response.data.data.data_access_expires_at;
      const isValidExpiry =
        dataAccessExpiresAt !== undefined &&
        dataAccessExpiresAt !== null &&
        !isNaN(dataAccessExpiresAt);

      // Tính thời gian hết hạn
      let expiresAt;
      let isNeverExpired = false;

      if (isPageToken) {
        // Page Access Token từ long-lived user token không có thời hạn
        expiresAt = null;
        isNeverExpired = true;
      } else if (isValidExpiry) {
        // Sử dụng thời gian hết hạn từ response
        expiresAt = dataAccessExpiresAt * 1000;
      } else {
        // Mặc định 60 ngày nếu không có giá trị hợp lệ
        expiresAt = Date.now() + 60 * 24 * 60 * 60 * 1000; // 60 ngày
      }

      return {
        isValid: response.data.data.is_valid === false ? false : true,
        expiresAt: expiresAt,
        isNeverExpired: isNeverExpired,
        tokenType: response.data.data.type || null,
        error: response.data.data.error || null,
      };
    }
    return { isValid: false, error: "Invalid response format" };
  } catch (error) {
    console.error("Error validating token:", error);

    // Cung cấp thông tin lỗi chi tiết hơn
    let errorMessage = error.message;

    if (error.response) {
      const { status, data } = error.response;

      if (status === 400) {
        if (data.error?.message) {
          errorMessage = `Facebook API Error: ${data.error.message}`;
        } else {
          errorMessage = "Token không hợp lệ hoặc đã hết hạn";
        }
      } else if (status === 401) {
        errorMessage = "Không có quyền truy cập. Kiểm tra App ID và App Secret";
      } else if (status === 403) {
        errorMessage = "Quyền truy cập bị từ chối";
      } else {
        errorMessage = `HTTP ${status}: ${
          data.error?.message || "Unknown error"
        }`;
      }

      console.error("📄 Facebook API Response:", {
        status,
        data,
        url: error.config?.url,
      });
    }

    return {
      isValid: false,
      error: errorMessage,
    };
  }
};

/**
 * Kiểm tra và làm mới token fanpage
 * @param {string} userId - ID của người dùng
 * @param {string} fanpageId - ID của fanpage
 * @returns {Promise<Object>} - Kết quả kiểm tra và làm mới
 */
export const checkAndRefreshFanpageToken = async (userId, fanpageId) => {
  try {
    const db = getDatabase();
    const fanpageRef = ref(
      db,
      `users/${userId}/connectedFanpages/${fanpageId}`
    );
    const snapshot = await get(fanpageRef);

    if (!snapshot.exists()) {
      return { success: false, error: "Fanpage không tìm thấy" };
    }

    const fanpage = snapshot.val();
    // Ưu tiên facebookAccessToken (luôn là FB token thật)
    // userAccessToken có thể là Firebase JWT nếu login bằng Google → gây hang FB.api
    const facebookAccessToken = localStorage.getItem("facebookAccessToken");
    const userAccessToken = localStorage.getItem("userAccessToken");
    const tokenForFBApi = facebookAccessToken || userAccessToken;

    if (!tokenForFBApi) {
      return { success: false, error: "Người dùng chưa đăng nhập" };
    }

    // Guard: JWT token (chứa dấu ".") không thể dùng cho Facebook API
    if (tokenForFBApi.includes(".")) {
      console.error("❌ checkAndRefreshFanpageToken: Token là JWT, không phải Facebook token");
      return { success: false, error: "Token không phải Facebook access token. Vui lòng kết nối lại fanpage." };
    }

    // Kiểm tra token hiện tại
    console.log("🔍 checkAndRefreshFanpageToken: Bắt đầu validateToken cho fanpage:", fanpageId);
    const tokenStatus = await validateToken(fanpage.access_token);
    console.log("🔍 checkAndRefreshFanpageToken: Kết quả validateToken:", { isValid: tokenStatus.isValid, error: tokenStatus.error });

    // Nếu token còn hợp lệ, cập nhật thời gian hết hạn và trả về
    if (tokenStatus.isValid) {
      console.log("✅ Token hợp lệ, cập nhật Firebase...");
      const updateData = {
        tokenStatus: "active",
        lastChecked: Date.now(),
        tokenExpiry: null,
        isNeverExpired: true,
      };

      await update(fanpageRef, updateData);
      return { success: true, fanpage };
    }

    // Nếu token không hợp lệ, thử lấy token mới
    try {
      // Thêm timeout 15s để tránh hang vĩnh viễn khi FB.api không phản hồi
      const response = await Promise.race([
        new Promise((resolve, reject) => {
          window.FB.api(
            `/me/accounts`,
            "get",
            { access_token: tokenForFBApi },
            function (response) {
              if (response && !response.error) {
                resolve(response);
              } else {
                reject(
                  response?.error ||
                    new Error("Không thể lấy access token cho fanpage")
                );
              }
            }
          );
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("FB.api timeout sau 15 giây")), 15000)
        ),
      ]);

      // Tìm fanpage tương ứng từ response
      const fbFanpage = response.data.find((page) => page.id === fanpageId);

      if (fbFanpage && fbFanpage.access_token) {
        try {
          // Bước 1: Chuyển đổi short-lived USER token thành long-lived USER token
          const exchangeResult = await exchangeForLongLivedToken(
            tokenForFBApi
          );

          if (exchangeResult.success) {
            // Bước 2: Sử dụng long-lived USER token để lấy long-lived PAGE token
            const pageTokenResult = await getPageAccessToken(
              exchangeResult.accessToken,
              fanpageId
            );

            if (pageTokenResult.success) {
              // Cập nhật token mới (long-lived PAGE token)
              // Page Access Token được tạo từ long-lived user token không có ngày hết hạn
              const updatedFanpage = {
                ...fanpage,
                access_token: pageTokenResult.accessToken,
                tokenStatus: "active",
                // Nếu expiresAt là null, đánh dấu là token không hết hạn
                tokenExpiry: null,
                isNeverExpired: true, // Thêm flag để đánh dấu token không bao giờ hết hạn
                lastRefreshed: Date.now(),
              };

              await update(fanpageRef, updatedFanpage);
              return { success: true, fanpage: updatedFanpage };
            } else {
              throw new Error(
                pageTokenResult.error || "Không thể lấy page access token"
              );
            }
          } else {
            throw new Error(
              exchangeResult.error ||
                "Không thể chuyển đổi sang long-lived user token"
            );
          }
        } catch (error) {
          console.warn(
            "Không thể lấy long-lived page token, sử dụng short-lived page token:",
            error.message
          );

          // Sử dụng short-lived page token nếu không thể lấy long-lived token
          // Theo tài liệu của Facebook, page token từ short-lived user token cũng có thời hạn ngắn
          // Để an toàn, chúng ta đặt thời hạn là 1 giờ
          const updatedFanpage = {
            ...fanpage,
            access_token: fbFanpage.access_token,
            tokenStatus: "active",
            tokenExpiry: null,
            lastRefreshed: Date.now(),
            tokenError: error.message || "Không thể lấy long-lived page token",
          };

          await update(fanpageRef, updatedFanpage);
          return { success: true, fanpage: updatedFanpage };
        }
      } else {
        // Cập nhật trạng thái token không hợp lệ
        await update(fanpageRef, {
          tokenStatus: "invalid",
          tokenError: "Không tìm thấy token mới",
        });
        return {
          success: false,
          error: "Không tìm thấy token mới cho fanpage",
        };
      }
    } catch (error) {
      // Cập nhật trạng thái lỗi
      await update(fanpageRef, {
        tokenStatus: "error",
        tokenError: error.message || "Unknown error",
      });
      return { success: false, error: error.message || "Unknown Error" };
    }
  } catch (error) {
    console.error("Error checking and refreshing fanpage token:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Wrapper function để kiểm tra token trước khi gọi Facebook API
 * @param {Object} fanpage - Thông tin fanpage
 * @param {Function} callback - Callback được gọi khi token hợp lệ
 * @param {Function} fallbackCallback - Callback được gọi khi token không hợp lệ
 * @returns {Promise<any>} - Kết quả từ callback
 */
export const withValidToken = async (
  fanpage,
  callback,
  fallbackCallback = null
) => {
  try {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      throw new Error("Người dùng chưa đăng nhập");
    }

    // Kiểm tra và làm mới token nếu cần
    console.log("🔍 withValidToken: Gọi checkAndRefreshFanpageToken...");
    const {
      success,
      fanpage: updatedFanpage,
      error,
    } = await checkAndRefreshFanpageToken(userId, fanpage.id);
    console.log("🔍 withValidToken: Kết quả:", { success, error });

    if (!success) {
      console.error("Token validation failed:", error);

      // Nếu có fallback callback, gọi nó
      if (fallbackCallback) {
        return await fallbackCallback(error);
      }

      throw new Error(`Token không hợp lệ: ${error}`);
    }

    // Gọi callback với fanpage đã được cập nhật token
    return await callback(updatedFanpage);
  } catch (error) {
    console.error("Error in withValidToken:", error);

    // Nếu có fallback callback, gọi nó
    if (fallbackCallback) {
      return await fallbackCallback(error);
    }

    throw error;
  }
};

/**
 * Kiểm tra tất cả token fanpage của người dùng
 * @returns {Promise<Object>} - Kết quả kiểm tra
 */
export const checkAllFanpageTokens = async () => {
  try {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      return { success: false, error: "Người dùng chưa đăng nhập" };
    }

    const db = getDatabase();
    const fanpagesRef = ref(db, `users/${userId}/connectedFanpages`);
    const snapshot = await get(fanpagesRef);

    if (!snapshot.exists()) {
      return { success: true, message: "Không có fanpage nào" };
    }

    const fanpages = snapshot.val();
    const results = {};
    let hasInvalidTokens = false;

    // Kiểm tra từng fanpage
    for (const [fanpageId, fanpage] of Object.entries(fanpages)) {
      // Kiểm tra fanpage có access_token hợp lệ không
      if (!fanpage.access_token) {
        console.warn(
          `⚠️ Fanpage ${fanpage.name} (${fanpageId}) không có access_token`
        );

        // Cập nhật trạng thái lỗi
        const updates = {
          tokenStatus: "invalid",
          tokenError: "Không có access_token",
          lastChecked: Date.now(),
          tokenExpiry: null,
          isNeverExpired: false,
        };

        await update(
          ref(db, `users/${userId}/connectedFanpages/${fanpageId}`),
          updates
        );

        results[fanpageId] = {
          name: fanpage.name,
          isValid: false,
          error: "Không có access_token",
        };

        hasInvalidTokens = true;
        continue; // Bỏ qua fanpage này
      }

      const tokenStatus = await validateToken(fanpage.access_token);

      // Cập nhật trạng thái token
      const updates = {
        tokenStatus: tokenStatus.isValid ? "active" : "invalid",
        lastChecked: Date.now(),
      };

      updates.tokenExpiry = null;
      updates.isNeverExpired = true;

      if (!tokenStatus.isValid) {
        updates.tokenError = tokenStatus.error;
        hasInvalidTokens = true;
      }

      await update(
        ref(db, `users/${userId}/connectedFanpages/${fanpageId}`),
        updates
      );

      results[fanpageId] = {
        name: fanpage.name,
        isValid: tokenStatus.isValid,
        error: tokenStatus.error,
      };
    }

    return {
      success: true,
      results,
      hasInvalidTokens,
    };
  } catch (error) {
    console.error("Error checking all fanpage tokens:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Kết nối lại fanpage
 * @param {Object} fanpage - Thông tin fanpage cần kết nối lại
 * @returns {Promise<Object>} - Kết quả kết nối lại
 */
export const reconnectFanpage = async (fanpage) => {
  try {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      return { success: false, error: "Người dùng chưa đăng nhập" };
    }

    // Yêu cầu quyền truy cập lại
    const loginResponse = await new Promise((resolve, reject) => {
      window.FB.login(
        (response) => {
          if (response.status === "connected") {
            resolve(response);
          } else {
            reject(new Error("Đăng nhập Facebook không thành công"));
          }
        },
        {
          // Yêu cầu đầy đủ quyền cần thiết để quản lý fanpage
          scope: FACEBOOK_MANAGEMENT_SCOPE,
          auth_type: "rerequest",
        }
      );
    });

    const userAccessToken = loginResponse.authResponse.accessToken;
    localStorage.setItem("userAccessToken", userAccessToken);

    // Lấy lại danh sách fanpage
    // Sử dụng axios thay vì FB.api để tránh hang
    const pagesResponse = await axios.get(
      `https://graph.facebook.com/v21.0/me/accounts`,
      {
        params: { access_token: userAccessToken },
        timeout: 15000,
      }
    );

    if (!pagesResponse.data || pagesResponse.data.error) {
      throw pagesResponse.data?.error || new Error("Không thể lấy danh sách fanpage");
    }

    // Tìm fanpage cần kết nối lại (axios.data = Graph API response, .data = mảng pages)
    const fbFanpage = pagesResponse.data.data.find((page) => page.id === fanpage.id);

    if (fbFanpage && fbFanpage.access_token) {
      // Cập nhật token mới
      const db = getDatabase();
      const fanpageRef = ref(
        db,
        `users/${userId}/connectedFanpages/${fanpage.id}`
      );

      try {
        // Bước 1: Chuyển đổi short-lived USER token thành long-lived USER token
        const exchangeResult = await exchangeForLongLivedToken(userAccessToken);

        if (exchangeResult.success) {
          // Bước 2: Sử dụng long-lived USER token để lấy long-lived PAGE token
          const pageTokenResult = await getPageAccessToken(
            exchangeResult.accessToken,
            fanpage.id
          );

          if (pageTokenResult.success) {
            // Cập nhật với long-lived PAGE token
            // Page Access Token được tạo từ long-lived user token không có ngày hết hạn
            await update(fanpageRef, {
              access_token: pageTokenResult.accessToken,
              tokenStatus: "active",
              // Nếu expiresAt là null, đánh dấu là token không hết hạn
              tokenExpiry: null,
              isNeverExpired: true, // Thêm flag để đánh dấu token không bao giờ hết hạn
              lastRefreshed: Date.now(),
              tokenError: null,
            });
          } else {
            throw new Error(
              pageTokenResult.error || "Không thể lấy page access token"
            );
          }
        } else {
          throw new Error(
            exchangeResult.error ||
              "Không thể chuyển đổi sang long-lived user token"
          );
        }
      } catch (error) {
        console.warn(
          "Không thể lấy long-lived page token, sử dụng short-lived page token:",
          error.message
        );

        // Sử dụng short-lived page token nếu không thể lấy long-lived token
        // Theo tài liệu của Facebook, page token từ short-lived user token cũng có thời hạn ngắn
        await update(fanpageRef, {
          access_token: fbFanpage.access_token,
          tokenStatus: "active",
          tokenExpiry: null,
          lastRefreshed: Date.now(),
          tokenError: error.message || "Không thể lấy long-lived page token",
        });
      }

      return {
        success: true,
        message: `Đã kết nối lại fanpage ${fanpage.name} thành công`,
      };
    } else {
      return {
        success: false,
        error: `Không tìm thấy fanpage ${fanpage.name} trong tài khoản của bạn`,
      };
    }
  } catch (error) {
    console.error("Error reconnecting fanpage:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Yêu cầu người dùng cấp thêm quyền quản lý và bình luận cho Fanpage (Rerequest)
 * @param {Object} fanpage - Fanpage cần cấp thêm quyền
 * @returns {Promise<Object>} - Kết quả cấp thêm quyền
 */
export const grantAdditionalCommentPermissions = async (fanpage) => {
  try {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      return { success: false, error: "Người dùng chưa đăng nhập" };
    }

    // Yêu cầu quyền truy cập nâng cao (bao gồm bình luận) qua rerequest
    const loginResponse = await new Promise((resolve, reject) => {
      window.FB.login(
        (response) => {
          if (response.status === "connected") {
            resolve(response);
          } else {
            reject(new Error("Đăng nhập Facebook không thành công hoặc người dùng đã hủy"));
          }
        },
        {
          scope: FACEBOOK_MANAGEMENT_SCOPE,
          auth_type: "rerequest",
        }
      );
    });

    const userAccessToken = loginResponse.authResponse.accessToken;
    localStorage.setItem("userAccessToken", userAccessToken);
    localStorage.setItem("facebookAccessToken", userAccessToken);

    // Lấy lại danh sách fanpage để tìm access_token mới của trang
    const pagesResponse = await axios.get(
      `https://graph.facebook.com/v21.0/me/accounts`,
      {
        params: { access_token: userAccessToken },
        timeout: 15000,
      }
    );

    if (!pagesResponse.data || pagesResponse.data.error) {
      throw pagesResponse.data?.error || new Error("Không thể lấy danh sách fanpage");
    }

    const fbFanpage = pagesResponse.data.data.find((page) => page.id === fanpage.id);

    if (fbFanpage && fbFanpage.access_token) {
      const db = getDatabase();
      const fanpageRef = ref(db, `users/${userId}/connectedFanpages/${fanpage.id}`);

      // Lấy danh sách scope đã được cấp thực tế
      const grantedScopes = await fetchGrantedFacebookScopes(userAccessToken);
      
      try {
        // Đổi sang long-lived user token và long-lived page token
        const exchangeResult = await exchangeForLongLivedToken(userAccessToken);

        if (exchangeResult.success) {
          const pageTokenResult = await getPageAccessToken(
            exchangeResult.accessToken,
            fanpage.id
          );

          if (pageTokenResult.success) {
            const metadata = buildCommentAutomationMetadata(grantedScopes);
            await update(fanpageRef, {
              access_token: pageTokenResult.accessToken,
              tokenStatus: "active",
              tokenExpiry: null,
              isNeverExpired: true,
              lastRefreshed: Date.now(),
              tokenError: null,
              ...metadata,
            });
            return {
              success: true,
              message: `Đã cấp thêm quyền bình luận cho fanpage ${fanpage.name} thành công`,
              commentAutomationEligible: metadata.commentAutomationEligible,
            };
          }
        }
      } catch (err) {
        console.warn("Lỗi khi chuyển đổi long-lived token khi cấp thêm quyền:", err);
      }

      // Fallback sang short-lived token nếu đổi long-lived lỗi
      const metadata = buildCommentAutomationMetadata(grantedScopes);
      await update(fanpageRef, {
        access_token: fbFanpage.access_token,
        tokenStatus: "active",
        tokenExpiry: null,
        lastRefreshed: Date.now(),
        tokenError: "Sử dụng short-lived token sau khi nâng cấp quyền",
        ...metadata,
      });

      return {
        success: true,
        message: `Đã nâng cấp quyền bình luận cho fanpage ${fanpage.name} (Short-lived Token)`,
        commentAutomationEligible: metadata.commentAutomationEligible,
      };
    } else {
      return {
        success: false,
        error: `Không tìm thấy fanpage ${fanpage.name} trong tài khoản Facebook của bạn`,
      };
    }
  } catch (error) {
    console.error("Error upgrading comment permissions for fanpage:", error);
    return { success: false, error: error.message };
  }
};
